import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "reactstrap";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import { FaFileInvoice, FaPlus } from "react-icons/fa";
import { fetchInvoices, generateInvoice } from "../../redux/actions/InvoiceActions";
import InvoiceFilters from "./InvoiceFilters";
import InvoiceTable from "./InvoiceTable";
import InvoiceModals from "./InvoiceModals";
import jsPDF from "jspdf";

const InvoiceManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    InvoiceReducer: { invoices, count: invoicesCount, loading },
    AuthReducer: { auth }
  } = useSelector((state) => state);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [paginate, setPaginate] = useState(10);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [actionDropdowns, setActionDropdowns] = useState({});
  const [generateModal, setGenerateModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [bookingId, setBookingId] = useState("");

  useEffect(() => {
    const params = {
      page: page + 1,
      limit: paginate,
      search,
      status: status !== 'all' ? status : undefined,
      type: type !== 'all' ? type : undefined,
      startDate: customStartDate || undefined,
      endDate: customEndDate || undefined
    };
    dispatch(fetchInvoices(params));
  }, [dispatch, page, search, paginate, status, type, dateRange, customStartDate, customEndDate]);

  const handleGenerateInvoice = () => {
    if (bookingId) {
      dispatch(generateInvoice(bookingId));
      setGenerateModal(false);
      setBookingId("");
    }
  };

  const handleExportInvoice = (invoiceId) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) {
      toast.error("Invoice not found");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('INVOICE', 105, 20, { align: 'center' });
    
    // Invoice details
    doc.setFontSize(12);
    doc.text(`Invoice #: ${invoice.invoice_number}`, 20, 40);
    doc.text(`Issue Date: ${new Date(invoice.issue_date).toLocaleDateString()}`, 20, 50);
    doc.text(`Due Date: ${new Date(invoice.due_date).toLocaleDateString()}`, 20, 60);
    doc.text(`Status: ${invoice.status}`, 150, 40);
    doc.text(`Type: ${invoice.invoice_type}`, 150, 50);
    
    // Billing information
    doc.text('Bill To:', 20, 80);
    doc.text(invoice.billed_to_name, 20, 90);
    doc.text(invoice.billed_to_address, 20, 100);
    
    // Amount
    doc.setFontSize(14);
    doc.text(`Total Amount: ₹${parseFloat(invoice.total_amount).toLocaleString()}`, 20, 120);
    
    // Transaction info
    if (invoice.transaction_id) {
      doc.setFontSize(12);
      doc.text(`Transaction ID: ${invoice.transaction_id}`, 20, 135);
    }
    
    // Notes
    if (invoice.notes) {
      doc.text('Notes:', 20, 210);
      doc.text(invoice.notes, 20, 220);
    }
    
    doc.save(`invoice-${invoice.invoice_number}.pdf`);
    toast.success("Invoice exported successfully");
  };

  const handleExportCSV = () => {
    if (!invoices?.length) return;
    const csvData = invoices.map((invoice, index) => ({
      "SL. No.": index + 1,
      "Invoice #": invoice.invoice_number,
      "Issue Date": new Date(invoice.issue_date).toLocaleDateString(),
      "Due Date": new Date(invoice.due_date).toLocaleDateString(),
      "Billed To": invoice.billed_to_name,
      "Type": invoice.invoice_type,
      "Total Amount": `Rs.${invoice.total_amount}`,
      "Status": invoice.status
    }));
    
    const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoices.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setType("all");
    setDateRange("all");
    setShowCustomRange(false);
    setCustomStartDate("");
    setCustomEndDate("");
    setPage(0);
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">
              <FaFileInvoice className="me-2" />
              Invoice Management
            </h5>
            <div className="d-flex gap-2">
              <Button color="primary" onClick={() => navigate("/invoices/create")}>
                <FaPlus className="me-1" />
                Create Invoice
              </Button>
              <Button color="outline-primary" onClick={() => setGenerateModal(true)}>
                Generate Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <InvoiceFilters
          search={search}
          setSearch={setSearch}
          status={status}
          setStatus={setStatus}
          type={type}
          setType={setType}
          dateRange={dateRange}
          setDateRange={setDateRange}
          showCustomRange={showCustomRange}
          setShowCustomRange={setShowCustomRange}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          exportDropdownOpen={exportDropdownOpen}
          setExportDropdownOpen={setExportDropdownOpen}
          handleExportCSV={handleExportCSV}
          clearFilters={clearFilters}
        />

        <InvoiceTable
          loading={loading}
          invoices={invoices}
          page={page}
          paginate={paginate}
          actionDropdowns={actionDropdowns}
          setActionDropdowns={setActionDropdowns}
          setSelectedInvoice={setSelectedInvoice}
          setViewModal={setViewModal}
          handleExportInvoice={handleExportInvoice}
        />

        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={invoicesCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      <InvoiceModals
        generateModal={generateModal}
        setGenerateModal={setGenerateModal}
        viewModal={viewModal}
        setViewModal={setViewModal}
        selectedInvoice={selectedInvoice}
        bookingId={bookingId}
        setBookingId={setBookingId}
        handleGenerateInvoice={handleGenerateInvoice}
      />
    </div>
  );
};

export default InvoiceManagement;